/* The sound of a day's jobs being finished.
 *
 * Synthesised rather than loaded: a .mp3 would be the only binary asset in
 * the bundle, would need a licence, and would cost a request on a NAS that
 * already serves this over a home connection. Six notes of Web Audio weigh
 * nothing and never 404.
 *
 * The tune is a rising major arpeggio landing an octave up, played on soft
 * bell-ish sines -- the shape of every "you did it" chime a child has heard,
 * which is the point. It must read as a reward within its first two notes,
 * because a five-year-old has already looked up by the third.
 */

/** C6 E6 G6 C7, then a fifth above to lift the ending off the tonic. */
const NOTES: { hz: number; at: number; hold: number }[] = [
  { hz: 1046.5, at: 0.0,  hold: 0.16 },
  { hz: 1318.5, at: 0.09, hold: 0.16 },
  { hz: 1568.0, at: 0.18, hold: 0.18 },
  { hz: 2093.0, at: 0.30, hold: 0.55 },
  { hz: 3136.0, at: 0.30, hold: 0.55 },
];

/* Quiet on purpose. This plays on a tablet held at arm's length in a kitchen,
   often at bedtime, and a jingle a parent has to apologise for gets muted for
   good the first evening it startles someone. */
const PEAK = 0.13;

/** Is the day's list finished?
 *
 *  A total of zero is NOT a finished day: a child with no jobs set up has
 *  0 of 0 done, and the day they are given their first chore is not the day
 *  they should be congratulated for having none. The same zero already made
 *  the tally on this card read as a broken counter twice.
 */
export function dayComplete(
  chart: { todayDone: number; todayTotal: number },
): boolean {
  return chart.todayTotal > 0 && chart.todayDone >= chart.todayTotal;
}

let ctx: AudioContext | null = null;

/* Browsers only let audio start from a user gesture, and the gesture here is
   the tap that ticks the last job off -- which finishes a round trip to the
   server before anyone knows the day is complete. So the context is opened
   during the tap and the notes are scheduled later, once the answer is in. */
export function unlockJingle(): void {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as {
      webkitAudioContext?: typeof AudioContext;
    }).webkitAudioContext;
    if (!Ctor) return;
    ctx ??= new Ctor();
    if (ctx.state === "suspended") void ctx.resume();
  } catch {
    /* No audio on this device. Silence is a fine outcome; the tick still
       happened and the screen still says so. */
  }
}

/** True if the device is set to play things down -- the closest thing a
 *  browser offers to "no surprise noises, please". */
function wantsCalm(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

export function playJingle(): void {
  if (wantsCalm()) return;
  unlockJingle();
  if (!ctx || ctx.state !== "running") return;

  const t0 = ctx.currentTime + 0.02;
  const out = ctx.createGain();
  out.gain.value = 1;
  out.connect(ctx.destination);

  for (const { hz, at, hold } of NOTES) {
    const start = t0 + at;

    /* Two sines a hair apart. A single sine is a test tone; the beating
       between a detuned pair is what makes it sound struck rather than
       emitted. */
    for (const cents of [-4, 4]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      osc.detune.value = cents;

      /* A bell's envelope: onto the note almost instantly, then decaying the
         whole time it sounds. Ramping to a small floor rather than zero
         because exponentialRampToValueAtTime cannot reach it. */
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(PEAK / 2, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + hold);

      osc.connect(gain).connect(out);
      osc.start(start);
      osc.stop(start + hold + 0.05);
    }
  }
}
